// What you could be learning, and what it would cost you.
//
// The pair is chosen when the app is BUILT today: data/pairs/<id>/ goes into the bundle and that is
// what it teaches. MYN-107 made the app read its pair at runtime; this is the other half of making
// that a CHOICE — a list of what exists, so a picker has something to pick from and a download has
// something to weigh itself against.
//
// WEIGHING BEFORE STARTING IS NOT A NICETY. MYN-94 learned it the expensive way: the clip fetcher
// counted attempts rather than arrivals, so against a server that refused every request the bar
// reached 100% over an empty device. A total has to come from the same declaration the download
// follows, which is why every file a pair is made of is named here with its size, rather than the
// app asking for a directory and hoping.
//
// GENERATED, NEVER HAND-MAINTAINED. scripts/build-catalogue.mjs measures what is actually on disk;
// test/catalogue.test.ts asserts the figures still match it. A catalogue that drifts from the files
// is worse than no catalogue, because the app would believe it.

import { pairId } from './item-key.js';
import type { Language, Pair } from './pair.js';

/** One file of a corpus, measured both ways: what comes down the wire, and what lands on disk. */
export interface CorpusFile {
  name: string;
  /** The file as it is stored and read. */
  bytes: number;
  /** The same file gzipped, which is what a server that knows its job actually sends. */
  gzip: number;
}

/** A pair's recordings, when it has any. Null while a pair is text-only. */
export interface AudioListing {
  /** Where the clips live, relative to the catalogue's origin. Ends in a slash. */
  prefix: string;
  format: string;
  /** Which voice rendered them, so two builds of the same pair are told apart. */
  voice: string;
  clips: number;
  bytes: number;
}

/** One installable course. Enough to describe it, and to price it, without downloading it. */
export interface PairListing {
  /** The directory it is generated in — 'zh-en'. Stable, and what a URL is built from. */
  id: string;
  learning: Language;
  explainedIn: Language;
  /**
   * What this pair has that others do not. Carried so a picker can say "with pinyin and stroke
   * order" before anything is fetched; the full declaration arrives with the corpus.
   */
  reading: Pair['reading'];
  level: Pair['level'];
  altScript: Pair['altScript'];
  hasStrokes: boolean;
  /** Where the corpus lives, relative to the origin, and every file that makes it up. */
  corpus: { prefix: string; files: CorpusFile[] };
  audio: AudioListing | null;
}

export interface Catalogue {
  version: number;
  generatedAt: string;
  /**
   * Where all of this is served from.
   *
   * Hong Kong, which is reachable from the mainland — the constraint that shaped this whole app.
   * In the data rather than in the code so a rebranded deployment changes one line, exactly as the
   * audio manifest's origin does.
   */
  origin: string;
  pairs: PairListing[];
}

/**
 * The key this pair's progress is stored under.
 *
 * Derived rather than carried, so there is one definition of it. `id` is where the files live and
 * this is what the rows are keyed by; they are different strings for a good reason — a directory
 * name is a build's business and shared/item-key.ts keys on what the pair DECLARES.
 */
export const listingKey = (listing: PairListing): string =>
  pairId({ learning: listing.learning, explainedIn: listing.explainedIn } as Pair);

/** A listing for the pair in hand, or undefined. Matched on what the pair declares, not on a path. */
export function listingFor(catalogue: Catalogue, pair: Pair): PairListing | undefined {
  const want = pairId(pair);
  return catalogue.pairs.find((p) => listingKey(p) === want);
}

/** The same, by directory id. */
export const listingById = (catalogue: Catalogue, id: string): PairListing | undefined =>
  catalogue.pairs.find((p) => p.id === id);

/**
 * What installing a pair costs, in the two numbers a person actually wants.
 *
 * `transfer` is what comes down the line and `disk` is what it takes up afterwards, and they are
 * far apart here: the Mandarin corpus is 6 MB of JSON that gzips to 1.4. Saying only one of them
 * would be wrong to somebody on a metered connection or wrong to somebody short of space.
 *
 * Audio is optional because it is the whole weight — 189 MB against 1.4 — and because the fetcher
 * MYN-94 built already brings it down afterwards, in deck order, as a background job rather than
 * a gate.
 */
export function installCost(
  listing: PairListing,
  opts: { audio?: boolean } = {},
): { transfer: number; disk: number } {
  let transfer = 0;
  let disk = 0;
  for (const file of listing.corpus.files) {
    transfer += file.gzip;
    disk += file.bytes;
  }
  if (opts.audio && listing.audio) {
    // Already compressed. Gzipping an mp3 saves nothing and the server will not try.
    transfer += listing.audio.bytes;
    disk += listing.audio.bytes;
  }
  return { transfer, disk };
}

/** Every file a pair is made of, as paths under the origin — the plan a download follows. */
export const corpusPaths = (listing: PairListing): string[] =>
  listing.corpus.files.map((f) => `${listing.corpus.prefix}${f.name}`);
